import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { useLiveQuery } from 'dexie-react-hooks';
import { PageHeader } from '@/components/PageHeader';
import { Card, Dato, Seccion } from '@/components/Card';
import { Banner } from '@/components/Banner';
import { Button } from '@/components/Button';
import { leerSettings, contarRegistros, VERSION_ESQUEMA_ACTUAL } from '@/db/schema';
import { estadoAlmacenamiento, formatearBytes, type EstadoAlmacenamiento } from '@/lib/storage';
import { describirUltimoRespaldo } from '@/domain/backup-reminder';
import { ETIQUETAS_TABLA, TABLAS } from '@/db/types';
import { detectarPlataforma } from '@/features/pwa/platform';
import { BuscarActualizacion } from './BuscarActualizacion';
import { VerificarIntegridad } from './VerificarIntegridad';

export function SettingsPage() {
  const settings = useLiveQuery(() => leerSettings(), []);
  const conteos = useLiveQuery(() => contarRegistros(), []);
  const [almacenamiento, setAlmacenamiento] = useState<EstadoAlmacenamiento | null>(null);
  const [plataforma] = useState(() => detectarPlataforma());

  useEffect(() => {
    let vigente = true;
    void estadoAlmacenamiento().then((estado) => {
      if (vigente) setAlmacenamiento(estado);
    });
    return () => {
      vigente = false;
    };
  }, []);

  const persistencia =
    almacenamiento?.persistente === true
      ? '✅ Concedida'
      : almacenamiento?.persistente === false
        ? '⚠️ No concedida'
        : 'No disponible';

  return (
    <div className="space-y-6">
      <PageHeader titulo="Ajustes" descripcion="Estado del almacenamiento y de la aplicacion" />

      <BuscarActualizacion />

      <VerificarIntegridad />

      <Seccion titulo="Almacenamiento de este dispositivo">
        <Card>
          <dl className="divide-y divide-borde">
            <Dato etiqueta="Espacio usado" valor={formatearBytes(almacenamiento?.usadoBytes ?? null)} />
            <Dato
              etiqueta="Espacio disponible"
              valor={formatearBytes(almacenamiento?.cuotaBytes ?? null)}
            />
            <Dato
              etiqueta="Almacenamiento persistente"
              valor={persistencia}
              detalle="Pide al navegador no borrar los datos por falta de espacio"
            />
          </dl>
        </Card>

        {almacenamiento?.persistente === false && (
          <Banner tono="aviso" titulo="El navegador no concedio almacenamiento persistente">
            En Safari esto es normal y no significa que algo este mal. La proteccion real en
            iPhone es usar la aplicacion instalada y hacer respaldos con frecuencia.
          </Banner>
        )}
      </Seccion>

      <Seccion titulo="Respaldos">
        <Card>
          <dl className="divide-y divide-borde">
            <Dato
              etiqueta="Ultimo respaldo"
              valor={describirUltimoRespaldo(settings?.ultimoRespaldoEn ?? null)}
            />
            <Dato
              etiqueta="Avisar despues de"
              valor={`${settings?.diasAvisoRespaldo ?? 7} dias`}
            />
          </dl>
          <div className="mt-3">
            <Link to="/respaldos">
              <Button variante="secundario">Ir a respaldos</Button>
            </Link>
          </div>
        </Card>
      </Seccion>

      <Seccion titulo="Registros guardados">
        <Card>
          <ul className="divide-y divide-borde text-sm">
            {TABLAS.filter((t) => t !== 'settings').map((tabla) => (
              <li key={tabla} className="flex justify-between gap-4 py-2">
                <span className="text-texto-suave">{ETIQUETAS_TABLA[tabla]}</span>
                <span className="font-medium text-texto">{conteos?.[tabla] ?? 0}</span>
              </li>
            ))}
          </ul>
        </Card>
      </Seccion>

      <Seccion titulo="Aplicacion">
        <Card>
          <dl className="divide-y divide-borde">
            <Dato etiqueta="Version del esquema" valor={String(VERSION_ESQUEMA_ACTUAL)} />
            <Dato etiqueta="Moneda" valor={settings?.moneda ?? 'MXN'} />
            <Dato
              etiqueta="Dias de anticipacion"
              valor={String(settings?.diasAnticipacionDefault ?? 1)}
              detalle="Cuantos dias antes se prepara la comida"
            />
            <Dato
              etiqueta="Modo de ejecucion"
              valor={plataforma.enStandalone ? '✅ App instalada' : '⚠️ Navegador'}
            />
          </dl>
          {!plataforma.enStandalone && (
            <div className="mt-3">
              <Link to="/instalar">
                <Button variante="secundario">Como instalar la aplicacion</Button>
              </Link>
            </div>
          )}
        </Card>
      </Seccion>

      <Banner tono="alerta" titulo="Tus datos estan solo en este telefono">
        No hay copia en la nube. Borrar los datos de navegacion en Ajustes de iPhone elimina
        toda la informacion de esta aplicacion, y no hay forma de recuperarla sin un respaldo
        exportado.
      </Banner>
    </div>
  );
}
